import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaDownload, FaExclamationCircle, FaCheckCircle, FaTimes, FaSync, FaWallet, FaCreditCard, FaUserCircle, FaPlus } from "react-icons/fa";

const InfluenerIDCards = () => {
  const [formData, setFormData] = useState({});
  const [channelTypes, setChannelTypes] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [platformLinks, setPlatformLinks] = useState([
    { platform: "", channelName: "", channelType: "", link: "" },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [reporterIdCards, setReporterIdCards] = useState([]);
  const [reporterImage, setReporterImage] = useState(null);
  const [channelLogo, setChannelLogo] = useState(null);
  const [reporterImagePreview, setReporterImagePreview] = useState(null);
  const [channelLogoPreview, setChannelLogoPreview] = useState(null);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [idCardExists, setIdCardExists] = useState(false);
  const [rejectionNote, setRejectionNote] = useState(null);

  // Validation states
  const [validationErrors, setValidationErrors] = useState({});

  // Demo preview and confirmation states
  const [showDemoPreview, setShowDemoPreview] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Cropping dependencies have been removed

  useEffect(() => {
    const checkIdCard = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/check/idcard`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("ID Card exists:", res);
        setIdCardExists(res.data.exists);
        // Handle different ID card statuses
        if (res.data.data && res.data.data.status) {
          const status = res.data.data.status.trim();

          if (status === "Under Review") {
            // Show demo for under review
            setReporterIdCards([res.data.data]);
          } else if (status === "Approved") {
            // Show message for approved status
            setIdCardExists('approved');
            setReporterIdCards([]);
          } else if (status === "Rejected") {
            // Allow resubmission for rejected applications
            setIdCardExists('rejected');
            setReporterIdCards([]);
            // Store rejection note if available
            setRejectionNote(res.data.data.rejectNote || null);
          } else {
            // Default case - allow new application
            setReporterIdCards([]);
          }
        } else {
          // No existing ID card - allow new application
          setReporterIdCards([]);
        }
      } catch (err) {
        console.error("Error checking ID card:", err);
      } finally {
        setLoading(false);
      }
    };
    checkIdCard();
  }, []);



  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/user/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        setFormData(res.data.data);
      } catch (err) {
        console.error("Error fetching profile:", err);
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const fetchAdPricing = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/ad/pricing`
        );
        setChannelTypes(res.data.data.channelType || []);
        setPlatforms(res.data.data.plateforms || []);
      } catch (err) {
        console.error("Error fetching ad pricing:", err);
      }
    };
    fetchAdPricing();
  }, []);

  // Enhanced validation function
  const validateForm = () => {
    const errors = {};

    if (!reporterImage) {
      errors.reporterImage = "Reporter Image is required";
    }
    if (!channelLogo) {
      errors.channelLogo = "Channel Logo is required";
    }

    platformLinks.forEach((entry, index) => {
      if (!entry.platform) {
        errors[`platform_${index}`] = "Platform is required";
      }
      if (!entry.channelName) {
        errors[`channelName_${index}`] = "Channel Name is required";
      }
      if (!entry.channelType) {
        errors[`channelType_${index}`] = "Channel Type is required";
      }
      if (!entry.link) {
        errors[`link_${index}`] = "Platform Link is required";
      }
    });

    if (!isTermsAccepted) {
      errors.terms = "You must accept the terms and conditions";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };


  // Cropping methods removed as requested.

  const handleConfirmDemo = () => {
    setShowDemoPreview(false);
    setShowConfirmation(true);
  };

  const handleCancelDemo = () => {
    setShowDemoPreview(false);
  };

  const handleFinalSubmit = async () => {
    setShowConfirmation(false);
    await handleConfirmSubmission();
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  const addPlatformLink = () => {
    setPlatformLinks([
      ...platformLinks,
      { platform: "", channelName: "", channelType: "", link: "" },
    ]);
  };

  const handlePlatformChange = (index, field, value) => {
    const updated = [...platformLinks];
    updated[index][field] = value;
    setPlatformLinks(updated);
  };

  const removePlatformLink = (index) => {
    const updated = [...platformLinks];
    updated.splice(index, 1);
    setPlatformLinks(updated);
  };

  const handleSubmit = async () => {
    // Use enhanced validation
    if (!validateForm()) {
      toast.error("Please fix all validation errors before submitting");
      return;
    }

    // Prevent multiple ID card generation - but allow resubmission for rejected applications
    if (reporterIdCards.length > 0 && idCardExists !== 'rejected') {
      toast.error("You have already generated your ID card. Only one is allowed.");
      return;
    }

    // Show demo preview first
    setShowDemoPreview(true);
  };

  const handleConfirmSubmission = async () => {
    setIsSubmitting(true);
    setIsUploading(true);
    const formDataToSend = new FormData();

    const platformData = {};
    platformLinks.forEach((entry) => {
      if (entry.platform && entry.link) {
        platformData[entry.platform] = entry.link;
      }
    });

    formDataToSend.append("channelName", platformLinks[0]?.channelName || "");
    formDataToSend.append("channelType", platformLinks[0]?.channelType || "");
    formDataToSend.append("plateform", platformLinks[0]?.platform || "");
    formDataToSend.append("platformLinks", JSON.stringify(platformData));

    if (reporterImage) formDataToSend.append("profileImage", reporterImage);
    if (channelLogo) formDataToSend.append("channelLogo", channelLogo);

    for (let [key, value] of formDataToSend.entries()) {
      if (value instanceof File) {
        console.log(`${key}:`, value.name);
      } else {
        console.log(`${key}:`, value);
      }
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/create/icard`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("ID Card Request Submitted Successfully! Please Wait For Approval 24 hours ");
      window.location.reload();
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message === 'You have already generated your ID card. Only one is allowed.') {
        toast.error("You have already generated your ID card. Only one is allowed.");
      } else {
        console.error("❌ Submit error:", err);
      }
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
    }
  };


  const handleReporterImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReporterImage(file);
      setReporterImagePreview(URL.createObjectURL(file));
    }
  };

  const handleChannelLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setChannelLogo(file);
      setChannelLogoPreview(URL.createObjectURL(file));
    }
  };

  const removeReporterImage = () => {
    setReporterImage(null);
    setReporterImagePreview(null);
    // Clear the file input
    const fileInput = document.querySelector(
      'input[type="file"][data-type="reporter"]'
    );
    if (fileInput) fileInput.value = "";
  };

  const removeChannelLogo = () => {
    setChannelLogo(null);
    setChannelLogoPreview(null);
    // Clear the file input
    const fileInput = document.querySelector(
      'input[type="file"][data-type="channel"]'
    );
    if (fileInput) fileInput.value = "";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-8 rounded-2xl shadow-xl text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Checking ID Card Status...</p>
        </div>
      </div>
    );
  }

  // Demo Preview Modal Component
  const DemoPreviewModal = () => {
    if (!showDemoPreview) return null;

    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-200 flex flex-col">
          {/* Compact Modal Header */}
          <div className="bg-gradient-to-r from-green-600 to-blue-600 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold">Preview Your ID Card</h2>
                  <p className="text-green-100 text-sm">Review your design before submission</p>
                </div>
              </div>
              <button
                onClick={handleCancelDemo}
                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>

          {/* Compact Demo Cards - Scrollable */}
          <div className="p-4 bg-gradient-to-br from-gray-50 to-blue-50 flex-1 overflow-y-auto">
            <div className="flex flex-col lg:flex-row justify-center gap-3 perspective-1000">
              {/* IINSAF Press Card - Compact */}
              <div className="flex scale-70 lg:scale-80">
                <div className="bg-gradient-to-b from-red-700 to-red-500 w-12 flex items-center justify-center rounded-l-lg shadow-lg">
                  <div className="whitespace-nowrap text-white font-bold text-xl tracking-[0.2em]" style={{ writingMode: "vertical-rl" }}>
                    INFLUENCER ID
                  </div>
                </div>
                <div className="bg-white/90 backdrop-blur-sm rounded-r-lg shadow-xl overflow-hidden w-full lg:w-[320px] border-t border-white/50 relative">
                  <div className="absolute top-2 -right-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white px-4 py-1 text-xs font-bold shadow-lg z-20 transform rotate-12 origin-center">
                    <div className="flex items-center gap-1">
                      <span>📋</span>
                      <span>DEMO</span>
                    </div>
                  </div>
                  <div className="p-4 relative">
                    <div className="absolute inset-0 opacity-5">
                      <div className="absolute -right-16 -top-16 w-32 h-32 rounded-full bg-red-600"></div>
                      <div className="absolute -left-16 -bottom-16 w-32 h-32 rounded-full bg-blue-600"></div>
                    </div>
                    <div className="flex flex-col items-center mb-4 relative">
                      <img src="/images/iinsaf.png" alt="IINSAF Logo" className="h-12 mb-2" />
                      <div className="text-center mb-2">
                        <p className="text-xs font-semibold text-gray-800">
                          <span className="text-red-600 font-bold">International Influencing</span>{" "}News Social Media Advertisement Federation
                        </p>
                      </div>
                      <h2 className="text-lg font-bold text-white bg-gradient-to-r from-red-600 to-red-700 px-4 py-1 rounded-full shadow-md mb-3">INFLUENCER</h2>
                      <p className="flex justify-between text-sm">
                        <span className="text-black font-medium">IINSAF ID :</span>
                        <span className="font-mono ml-2">ID-{Math.random().toString(36).substr(2, 4).toUpperCase()}-{Math.random().toString(36).substr(2, 4).toUpperCase()}</span>
                      </p>
                    </div>
                    <div className="flex flex-col items-center mb-4">
                      <div className="w-32 h-40 bg-gray-200 rounded-lg overflow-hidden mb-3 shadow-lg border-2 border-red-200 relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                        {reporterImagePreview ? (
                          <img src={reporterImagePreview} alt="Reporter" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-black">
                      <h3 className="text-lg font-semibold mb-3 text-center bg-clip-text text-transparent bg-gradient-to-r from-red-600 to-blue-800">{formData.name}</h3>
                      <div className="space-y-2 bg-white/50 p-3 rounded-lg text-sm">
                        <p className="flex justify-between"><span className="text-black font-medium">Phone:</span><span className="font-mono">{formData.mobile}</span></p>
                        <p className="flex justify-between"><span className="text-black font-medium">DOB:</span><span className="font-mono">{formData.dateOfBirth}</span></p>
                        <p className="flex justify-between"><span className="text-black font-medium">Blood Type:</span><span className="font-mono font-bold text-red-600">{formData.bloodType}</span></p>
                        <p className="flex justify-between"><span className="text-black font-medium">Issue Date:</span><span className="font-mono">{new Date().toLocaleDateString()}</span></p>
                        <p className="flex justify-between"><span className="text-black font-medium">Valid Up To:</span><span className="font-mono font-bold">{new Date().getFullYear() + 1}</span></p>
                        <p className="flex justify-between mt-3"><span className="text-black font-medium">Approved By:</span><span className="italic text-xs">IINSAF Authority</span></p>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-red-400 text-center">
                      <p className="text-xs text-black font-medium">Valid Until: <span className="font-bold">{new Date().getFullYear() + 1}</span></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* IINSAF ID Card - Compact */}
              <div className="flex scale-70 lg:scale-80">
                <div className="bg-gradient-to-b from-red-700 to-red-500 w-12 flex items-center justify-center rounded-l-lg shadow-lg">
                  <div className="whitespace-nowrap text-white font-bold text-3xl tracking-[0.2em]" style={{ writingMode: "vertical-rl" }}>
                    ii<span className="text-black">nsaf</span>
                  </div>
                </div>
                <div className="bg-white/90 backdrop-blur-sm rounded-r-lg shadow-xl overflow-hidden w-full lg:w-[320px] border-t border-white/50 relative">
                  <div className="absolute top-2 -right-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white px-4 py-1 text-xs font-bold shadow-lg z-20 transform rotate-12 origin-center">
                    <div className="flex items-center gap-1">
                      <span>📋</span>
                      <span>DEMO</span>
                    </div>
                  </div>
                  <div className="p-4 relative">
                    <img src="/CompanyImages/insaaf_logo-1.png" alt="Logo" className="h-16 w-16 -mb-10 object-contain" />
                    <div className="absolute inset-0 opacity-5">
                      <div className="absolute -right-16 -top-16 w-32 h-32 rounded-full bg-red-600"></div>
                      <div className="absolute -left-16 -bottom-16 w-32 h-32 rounded-full bg-blue-600"></div>
                    </div>
                    <div className="flex flex-col items-center mb-4">
                      <h2 className="text-lg font-bold text-white bg-gradient-to-r from-red-600 to-red-700 px-4 py-1 rounded-full shadow-md mb-3">INFLUENCER</h2>
                      <span className="text-sm font-semibold text-black">{platformLinks[0]?.channelName || "Channel Name"}</span>
                    </div>
                    <div className="flex flex-col items-center mb-4">
                      <div className="w-32 h-40 bg-gray-200 rounded-lg overflow-hidden mb-3 shadow-lg border-2 border-red-200 relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                        {channelLogoPreview ? (
                          <img src={channelLogoPreview} alt="channel logo" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-black">
                      <div className="space-y-2 bg-white/50 p-3 rounded-lg text-sm">
                        <p className="flex justify-between"><span className="text-black font-medium">Aadhar No:</span><span className="font-mono">{formData.aadharNo}</span></p>
                        <p className="flex justify-between"><span className="text-black font-medium">Address:</span><span className="text-right flex-1 ml-2 text-xs">{formData.residenceaddress}</span></p>
                        <p className="flex justify-between"><span className="text-black font-medium">State:</span><span className="text-right flex-1 ml-2 text-xs">{formData.state}</span></p>
                        <p className="flex justify-between"><span className="text-black font-medium">City:</span><span className="text-right flex-1 ml-2 text-xs">{formData.city}</span></p>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 pb-8 border-t border-red-400 text-center space-y-1">
                      <div className="flex justify-center">
                        <div className="h-16 w-16 p-1 bg-white rounded-lg shadow-md">
                          <img src="/images/qr-code.png" alt="QR Code" className="h-full w-full" />
                        </div>
                      </div>
                      <p className="text-xs text-black font-medium">www.iinsaf.com</p>
                      <p className="text-[10px] text-black font-bold uppercase">(RNI:HRBIL/25/A2063)</p>
                      <p className="text-[9px] text-gray-500 font-bold italic">Digitally Verified Identity</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Compact Modal Footer - Sticky */}
          <div className="bg-gray-50 p-4 border-t border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-600">
                <span className="font-medium">Note:</span> Review all details before confirming.
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancelDemo}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors text-sm"
                >
                  Back to Edit
                </button>
                <button
                  onClick={handleConfirmDemo}
                  className="px-6 py-2 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg transition-all text-sm"
                >
                  Confirm & Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Confirmation Modal Component
  const ConfirmationModal = () => {
    if (!showConfirmation) return null;

    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full border border-gray-200">
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Confirm ID Card Submission</h2>
            <p className="text-lg text-gray-600 mb-6">
              Are you sure you want to submit your ID card application? This action cannot be undone.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div className="text-left">
                  <h4 className="text-sm font-semibold text-blue-800 mb-1">Important Notice</h4>
                  <p className="text-xs text-blue-700">
                    Your ID card will be reviewed by IINSAF Authority. You will be notified once it's approved and ready for download.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={handleCancelConfirmation}
                className="px-8 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleFinalSubmit}
                className="px-8 py-3 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg transition-all"
              >
                Yes, Submit Application
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Cropping Modal completely removed.

  const UploadingModal = () => {
    if (!isUploading) return null;

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center flex flex-col items-center">
          <div className="relative mb-6">
            <div className="w-16 h-16 border-4 border-slate-100 border-t-indigo-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2 h-2 bg-indigo-600 rounded-full animate-pulse"></div>
            </div>
          </div>
          <h3 className="text-xl font-black text-slate-900 mb-2">Almost there...</h3>
          <p className="text-sm font-bold text-slate-500">
            Please wait 10-15 seconds. <br /> We are submitting your details.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      <DemoPreviewModal />
      <ConfirmationModal />
      <UploadingModal />
      {idCardExists === 'approved' ? (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl p-10 rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] border border-white text-center max-w-sm w-full transform animate-scaleUp">
            <div className="w-16 h-16 bg-green-50 text-green-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm border border-green-100">
              <FaCheckCircle className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-black text-slate-900 mb-3 tracking-tight">Access Granted</h2>
            <p className="text-xs font-bold text-slate-500 mb-8 leading-relaxed">
              Your credentials have been verified. Your official Influencer ID is now active and ready for your professional use.
            </p>
            <a href="/influencer/id-card" className="flex items-center justify-center gap-2 w-full py-4 bg-slate-900 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-lg shadow-slate-200 hover:bg-slate-800 transition-all active:scale-95">
              <FaDownload className="w-3 h-3" />
              Download Official Card
            </a>
          </div>
        </div>
      ) : idCardExists === 'rejected' ? (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl p-8 rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] border border-white text-center max-w-[360px] w-full transform animate-scaleUp">
            <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm border border-rose-100">
              <FaExclamationCircle className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-black text-rose-600 mb-3 tracking-tight">Review Feedback</h2>

            {rejectionNote && (
              <div className="bg-rose-50/50 border border-rose-100/50 rounded-2xl p-4 mb-6 text-left">
                <h4 className="text-[9px] font-black text-rose-500 uppercase tracking-widest mb-1.5">Authority Note</h4>
                <p className="text-[11px] font-bold text-rose-700 leading-relaxed">
                  {rejectionNote}
                </p>
              </div>
            )}

            <button
              onClick={() => {
                setIdCardExists(false);
                setReporterIdCards([]);
                setRejectionNote(null);
              }}
              className="w-full py-4 bg-rose-500 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-lg shadow-rose-100 hover:bg-rose-600 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              <FaPlus className="w-3 h-3" />
              Submit New Application
            </button>
          </div>
        </div>
      ) : idCardExists && reporterIdCards.length > 0 ? (
        <>
          <p className="text-center text-red-600 font-semibold py-8">You have already submitted an ID Card request.</p>
          <div className="space-y-12">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-600 to-blue-800">
                Your IINSAF Cards (Demo)
              </span>
            </h2>
            {reporterIdCards.map((card, index) => (
              <div key={card._id || index} className="flex flex-col lg:flex-row justify-center gap-8 px-4 perspective-1000 mb-12">
                {/* IINSAF Press Card - Compact Professional Demo */}
                <div className="flex scale-90 sm:scale-100 max-w-full overflow-hidden rounded-[2rem] shadow-2xl shadow-indigo-100">
                  {/* Vertical Branding */}
                  <div className="bg-slate-900 w-10 flex items-center justify-center">
                    <div className="whitespace-nowrap text-white font-black text-xs uppercase tracking-[0.3em] -rotate-180" style={{ writingMode: "vertical-rl" }}>
                      Official Influencer Credentials
                    </div>
                  </div>

                  {/* Main Card */}
                  <div className="bg-white p-5 w-full sm:w-[320px] relative overflow-hidden">
                    <div className="absolute top-2 -right-8 bg-amber-400 text-slate-900 px-8 py-1 text-[8px] font-black uppercase tracking-widest transform rotate-45 shadow-sm">
                      Under Review
                    </div>

                    <div className="flex flex-col items-center gap-4">
                      <img src="/images/iinsaf.png" alt="Logo" className="h-10 object-contain" />

                      <div className="w-24 h-32 bg-slate-50 rounded-2xl border-4 border-white shadow-xl overflow-hidden relative">
                        <img src={card.image || "/images/placeholder.png"} alt="Reporter" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                      </div>

                      <div className="text-center">
                        <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">{card.name}</h3>
                        <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest mt-0.5">{card.designation || "Digital Creator"}</p>
                      </div>

                      <div className="w-full space-y-2 bg-slate-50/50 p-4 rounded-2xl border border-slate-100">
                        <div className="flex justify-between items-center">
                          <span className="text-[8px] font-black text-slate-400 uppercase">IINSAF ID</span>
                          <span className="text-[9px] font-black text-slate-900 select-none blur-[2px]">ID-8821-XX</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-[8px] font-black text-slate-400 uppercase">Verified Mobile</span>
                          <span className="text-[9px] font-black text-slate-900">{card.mobileNo}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-[8px] font-black text-slate-400 uppercase">Valid Thru</span>
                          <span className="text-[9px] font-black text-indigo-600 uppercase">{new Date().getFullYear() + 1}</span>
                        </div>
                      </div>

                      <div className="w-full flex justify-between items-center px-2">
                        <div className="text-left">
                          <p className="text-[7px] font-black text-slate-300 uppercase">Authorized By</p>
                          <p className="text-[8px] font-black text-slate-600 uppercase">IINS Federation</p>
                        </div>
                        <div className="w-10 h-10 p-1 bg-white rounded-lg shadow-sm">
                          <img src="/images/qr-code.png" className="w-full h-full opacity-20" alt="QR" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="min-h-screen p-3 sm:p-6 pb-20">
          <div className="max-w-2xl mx-auto space-y-4">
            {/* Professional Header */}
            <div className="text-center py-4">
              <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-2xl shadow-sm border border-slate-100 mb-3">
                <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <FaCreditCard className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                  ID Card Application
                </h1>
              </div>
              <p className="text-sm font-semibold text-slate-600 uppercase tracking-widest px-4">Create your official professional identity</p>
            </div>

            {/* Main Form Container */}
            <div className="space-y-4">
              {/* Personal Information Section - Compact Card */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-5 py-4 bg-slate-50 border-b border-slate-100 flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded-xl shadow-sm flex items-center justify-center text-indigo-600">
                    <FaUserCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-xs font-black text-slate-900 uppercase tracking-tight">Identity Details</h2>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Verified Information</p>
                  </div>
                </div>

                <div className="p-5 grid grid-cols-2 gap-x-4 gap-y-4">
                  {[
                    { label: 'Full Name', value: formData.name },
                    { label: 'Designation', value: formData.role },
                    { label: 'Mobile', value: formData.mobile },
                    { label: 'Aadhar', value: formData.aadharNo },
                    { label: 'DOB', value: formData.dateOfBirth },
                    { label: 'Blood Group', value: formData.bloodType },
                    { label: 'State', value: formData.state },
                    { label: 'City', value: formData.city },
                  ].map((field, i) => (
                    <div key={i} className="space-y-1">
                      <label className="text-xs font-black text-slate-700 uppercase tracking-widest ml-1">{field.label}</label>
                      <div className="px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900">
                        {field.value || "—"}
                      </div>
                    </div>
                  ))}
                  <div className="col-span-2 space-y-1">
                    <label className="text-xs font-black text-slate-700 uppercase tracking-widest ml-1">Residential Address</label>
                    <div className="px-3 py-2 bg-slate-50/50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 leading-relaxed">
                      {formData.residenceaddress || "—"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Photo Upload Section */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-5 py-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white rounded-xl shadow-sm flex items-center justify-center text-rose-500">
                      <FaUserCircle className="w-4 h-4" />
                    </div>
                    <div>
                      <h2 className="text-sm font-black text-slate-900 uppercase tracking-tight">Identity Photos</h2>
                      <p className="text-xs font-bold text-slate-600 uppercase tracking-widest">Required for Verification</p>
                    </div>
                  </div>
                </div>

                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Reporter Image Upload */}
                  <div className={`relative p-4 rounded-2xl border-2 transition-all ${validationErrors.reporterImage ? 'border-red-400 bg-red-50/30' : reporterImagePreview ? 'border-indigo-200 border-solid bg-indigo-50/30' : 'border-dashed border-slate-300 bg-slate-50/50'}`}>
                    <label className="block text-[11px] font-black text-slate-700 uppercase tracking-widest mb-3">Influencer Photo</label>
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative group">
                        <div className={`w-20 h-24 rounded-xl shadow-sm border-2 border-white overflow-hidden flex items-center justify-center ${!reporterImagePreview ? 'bg-white' : ''}`}>
                          {reporterImagePreview ? (
                            <img src={reporterImagePreview} className="w-full h-full object-cover" alt="Reporter" />
                          ) : (
                            <FaUserCircle className="w-8 h-8 text-slate-200" />
                          )}
                        </div>
                        {reporterImagePreview && (
                          <button onClick={removeReporterImage} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-500 text-white rounded-full flex items-center justify-center shadow-lg active:scale-90">
                            <FaTimes className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                      <input type="file" accept="image/*" onChange={handleReporterImageChange} className="absolute inset-0 opacity-0 cursor-pointer" />
                      <p className="text-[10px] font-black text-indigo-700 uppercase tracking-tighter">
                        {reporterImagePreview ? "Change Image" : "Upload Image"}
                      </p>
                      {validationErrors.reporterImage && <p className="text-[10px] font-bold text-red-500">{validationErrors.reporterImage}</p>}
                    </div>
                  </div>

                  {/* Channel Logo Upload */}
                  <div className={`relative p-4 rounded-2xl border-2 transition-all ${validationErrors.channelLogo ? 'border-red-400 bg-red-50/30' : channelLogoPreview ? 'border-indigo-200 border-solid bg-indigo-50/30' : 'border-dashed border-slate-300 bg-slate-50/50'}`}>
                    <label className="block text-[11px] font-black text-slate-700 uppercase tracking-widest mb-3">Channel Logo</label>
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative group">
                        <div className={`w-20 h-24 rounded-xl shadow-sm border-2 border-white overflow-hidden flex items-center justify-center ${!channelLogoPreview ? 'bg-white' : ''}`}>
                          {channelLogoPreview ? (
                            <img src={channelLogoPreview} className="w-full h-full object-cover" alt="Logo" />
                          ) : (
                            <FaSync className="w-8 h-8 text-slate-200" />
                          )}
                        </div>
                        {channelLogoPreview && (
                          <button onClick={removeChannelLogo} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-500 text-white rounded-full flex items-center justify-center shadow-lg active:scale-90">
                            <FaTimes className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                      <input type="file" accept="image/*" onChange={handleChannelLogoChange} className="absolute inset-0 opacity-0 cursor-pointer" />
                      <p className="text-[10px] font-black text-indigo-700 uppercase tracking-tighter">
                        {channelLogoPreview ? "Change Logo" : "Upload Logo"}
                      </p>
                      {validationErrors.channelLogo && <p className="text-[10px] font-bold text-red-500">{validationErrors.channelLogo}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Platforms Links */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-5 py-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white rounded-xl shadow-sm flex items-center justify-center text-indigo-600">
                      <FaSync className="w-4 h-4" />
                    </div>
                    <div>
                      <h2 className="text-sm font-black text-slate-900 uppercase tracking-tight">Social Platforms</h2>
                      <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Connect Your Channels</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  {platformLinks.map((entry, idx) => (
                    <div key={idx} className="relative p-4 rounded-2xl bg-slate-50/50 border border-slate-200 group">
                      <div className="grid grid-cols-1 gap-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-700 uppercase tracking-widest ml-1">Platform</label>
                            <select
                              value={entry.platform}
                              onChange={(e) => handlePlatformChange(idx, "platform", e.target.value)}
                              className={`w-full text-sm font-bold px-3 py-2.5 bg-white border ${validationErrors[`platform_${idx}`] ? 'border-red-400 focus:ring-red-500' : 'border-slate-300 focus:ring-indigo-500'} rounded-xl outline-none focus:ring-2 focus:ring-opacity-20 transition-all text-slate-900`}
                            >
                              <option value="">Select</option>
                              {platforms.map((p, i) => <option key={i} value={p}>{p}</option>)}
                            </select>
                            {validationErrors[`platform_${idx}`] && <p className="text-[10px] font-bold text-red-500 mt-1">{validationErrors[`platform_${idx}`]}</p>}
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] font-black text-slate-700 uppercase tracking-widest ml-1">Type</label>
                            <select
                              value={entry.channelType}
                              onChange={(e) => handlePlatformChange(idx, "channelType", e.target.value)}
                              className={`w-full text-sm font-bold px-3 py-2.5 bg-white border ${validationErrors[`channelType_${idx}`] ? 'border-red-400 focus:ring-red-500' : 'border-slate-300 focus:ring-indigo-500'} rounded-xl outline-none focus:ring-2 focus:ring-opacity-20 transition-all text-slate-900`}
                            >
                              <option value="">Select</option>
                              {channelTypes.map((ct) => <option key={ct._id} value={ct.name}>{ct.name}</option>)}
                            </select>
                            {validationErrors[`channelType_${idx}`] && <p className="text-[10px] font-bold text-red-500 mt-1">{validationErrors[`channelType_${idx}`]}</p>}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-black text-slate-700 uppercase tracking-widest ml-1">Channel Name</label>
                          <input
                            type="text"
                            placeholder="Enter Name"
                            value={entry.channelName}
                            onChange={(e) => handlePlatformChange(idx, "channelName", e.target.value)}
                            className={`w-full text-sm font-bold px-3 py-2.5 bg-white border ${validationErrors[`channelName_${idx}`] ? 'border-red-400 focus:ring-red-500' : 'border-slate-300 focus:ring-indigo-500'} rounded-xl outline-none focus:ring-2 focus:ring-opacity-20 transition-all text-slate-900`}
                          />
                          {validationErrors[`channelName_${idx}`] && <p className="text-[10px] font-bold text-red-500 mt-1">{validationErrors[`channelName_${idx}`]}</p>}
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] font-black text-slate-700 uppercase tracking-widest ml-1">Profile Link</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="https://..."
                              value={entry.link}
                              onChange={(e) => handlePlatformChange(idx, "link", e.target.value)}
                              className={`flex-1 text-sm font-bold px-3 py-2.5 bg-white border ${validationErrors[`link_${idx}`] ? 'border-red-400 focus:ring-red-500' : 'border-slate-300 focus:ring-indigo-500'} rounded-xl outline-none focus:ring-2 focus:ring-opacity-20 transition-all text-slate-900`}
                            />
                            {idx > 0 && (
                              <button onClick={() => removePlatformLink(idx)} className="w-10 h-10 bg-rose-50 text-rose-500 rounded-xl flex items-center justify-center hover:bg-rose-100 transition-colors">
                                <FaTimes className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          {validationErrors[`link_${idx}`] && <p className="text-[10px] font-bold text-red-500 mt-1">{validationErrors[`link_${idx}`]}</p>}
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={addPlatformLink}
                    className="w-full py-3 rounded-2xl border-2 border-dashed border-indigo-200 text-indigo-700 text-xs font-black uppercase tracking-widest hover:bg-indigo-50 transition-all flex items-center justify-center gap-2"
                  >
                    <span>+ Add Another Platform</span>
                  </button>
                </div>
              </div>

              {/* Submit Section */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5 space-y-4">
                <div className="flex items-start gap-3 flex-col sm:flex-row">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={isTermsAccepted}
                      onChange={(e) => setIsTermsAccepted(e.target.checked)}
                      className="w-5 h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 mt-1 sm:mt-0 cursor-pointer"
                    />
                    <label htmlFor="terms" className={`text-xs font-bold leading-relaxed cursor-pointer select-none ${validationErrors.terms ? 'text-red-600' : 'text-slate-600'}`}>
                      I solemnly declare that all provided identity and channel details are authentic. I accept full responsibility for the information submitted for official Influencer ID generation.
                    </label>
                  </div>
                  {validationErrors.terms && <span className="text-[10px] font-black text-red-500 ml-8 sm:ml-0 bg-red-50 px-2 py-1 rounded-md">{validationErrors.terms}</span>}
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full py-4 bg-slate-900 hover:bg-indigo-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-slate-200 transition-all active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <FaSync className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <FaCheckCircle className="w-4 h-4" />
                      <span>Submit Application</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



export default InfluenerIDCards;
